import { baseApi, defaultQueryFreshness } from "./baseApi";

export interface PlanSummary {
  code: "free" | "pro" | "elite";
  name: string;
  monthlyPriceCents: number;
  currency: string;
  maxActiveBounties: number;
  maxActiveTrades: number;
  monthlyFreeBoosts: number;
  paidBoostPriceCents: number;
  postLifetimeDays: number;
  boostExtensionDays: number;
  feedRankWeight: number;
  themesEnabled: boolean;
  badgeProEligible: boolean;
  badgeEliteEligible: boolean;
}

export interface MonetizationUsage {
  yearMonth: string;
  activeTrades: number;
  activeBounties: number;
  freeBoostsUsed: number;
  freeBoostsRemaining: number;
  paidBoostsUsed: number;
}

export interface MonetizationMe {
  userId: string;
  activePlanCode: "free" | "pro" | "elite";
  planSelectionCompleted: boolean;
  plan: PlanSummary;
  usage: MonetizationUsage;
  profileTheme?: string | null;
  badges: Array<{ badgeType: string; earnedAt: string }>;
  subscription?: SubscriptionLifecycle | null;
}

export interface SubscriptionLifecycle {
  provider: "paypal";
  providerSubscriptionId: string;
  planCode: "free" | "pro" | "elite" | null;
  planName: string | null;
  subscriptionStatus: "pending" | "active" | "cancelled" | "past_due" | "suspended" | "expired";
  cycleStartsAt: string | null;
  cycleEndsAt: string | null;
  nextBillingAt: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  lastPaymentAt: string | null;
  downgradeEffectiveAt: string | null;
}

export interface BoostQuote {
  postType: "trade" | "bounty";
  postId: string;
  planCode: "free" | "pro" | "elite";
  currency: string;
  freeBoostsMonthly: number;
  freeBoostsUsed: number;
  freeBoostsRemaining: number;
  chargeCents: number;
  isFree: boolean;
  boostExtensionDays: number;
  paypalProductId?: string | null;
}

export interface PaypalBoostOrderResponse {
  requiresPayment: boolean;
  message?: string;
  orderId?: string;
  status?: string;
  paypalProductId?: string | null;
  links?: Array<{ href: string; rel: string; method: string }>;
  quote: BoostQuote;
}

export interface DowngradeToFreeResponse {
  message: string;
  activePlanCode: "free" | "pro" | "elite";
  subscription: SubscriptionLifecycle | null;
}

export const monetizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlans: builder.query<PlanSummary[], void>({
      query: () => ({ url: "/monetization/plans", method: "GET", usePublic: true }),
      providesTags: ["Monetization"],
      ...defaultQueryFreshness,
    }),
    getMonetizationMe: builder.query<MonetizationMe, void>({
      query: () => ({ url: "/monetization/me", method: "GET" }),
      providesTags: ["Monetization", "Billing", "User"],
      ...defaultQueryFreshness,
    }),
    selectFreePlan: builder.mutation<{ message: string; activePlanCode: string; planSelectionCompleted: boolean }, void>({
      query: () => ({ url: "/monetization/plan/select-free", method: "POST" }),
      invalidatesTags: ["Monetization", "Billing", "User", "Auth"],
    }),
    activatePaypalSubscription: builder.mutation<
      { message: string; activePlanCode: string; subscriptionId: string },
      { subscriptionId: string; planCode?: "free" | "pro" | "elite" }
    >({
      query: (payload) => ({ url: "/monetization/paypal/subscriptions/activate", method: "POST", data: payload }),
      invalidatesTags: ["Monetization", "Billing", "User", "Auth"],
    }),
    downgradePaypalSubscriptionToFree: builder.mutation<DowngradeToFreeResponse, void>({
      query: () => ({ url: "/monetization/paypal/subscriptions/downgrade-to-free", method: "POST" }),
      invalidatesTags: ["Monetization", "Billing", "User", "Auth"],
    }),
    quoteBoost: builder.mutation<BoostQuote, { postType: "trade" | "bounty"; postId: string }>({
      query: (payload) => ({ url: "/monetization/boosts/quote", method: "POST", data: payload }),
    }),
    applyFreeBoost: builder.mutation<unknown, { postType: "trade" | "bounty"; postId: string }>({
      query: (payload) => ({ url: "/monetization/boosts/apply-free", method: "POST", data: payload }),
      invalidatesTags: ["Monetization", "Billing", "Trades", "Bounties"],
    }),
    createBoostOrder: builder.mutation<PaypalBoostOrderResponse, { postType: "trade" | "bounty"; postId: string }>({
      query: (payload) => ({
        url: "/monetization/boosts/paypal/order",
        method: "POST",
        data: payload,
        timeout: 60000,
      }),
    }),
    captureBoostOrder: builder.mutation<unknown, { orderId: string; postType: "trade" | "bounty"; postId: string }>({
      query: ({ orderId, ...payload }) => ({
        url: `/monetization/boosts/paypal/order/${encodeURIComponent(orderId)}/capture`,
        method: "POST",
        data: payload,
        timeout: 60000,
      }),
      invalidatesTags: ["Monetization", "Billing", "Trades", "Bounties"],
    }),
    updateProfileTheme: builder.mutation<{ message: string; profileTheme: string | null }, { theme: string }>({
      query: (payload) => ({ url: "/monetization/profile/theme", method: "PATCH", data: payload }),
      invalidatesTags: ["Monetization", "User"],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetMonetizationMeQuery,
  useSelectFreePlanMutation,
  useActivatePaypalSubscriptionMutation,
  useDowngradePaypalSubscriptionToFreeMutation,
  useQuoteBoostMutation,
  useApplyFreeBoostMutation,
  useCreateBoostOrderMutation,
  useCaptureBoostOrderMutation,
  useUpdateProfileThemeMutation,
} = monetizationApi;
